import { useEffect, useState } from "react"
import { Form } from "antd"
import { Modal } from "components/ui/modals/Modal/Modal"
import { Tag } from "components/ui/tag/Tag"
import { Input } from "components/ui/inputs/Input"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { TrashIcon } from "components/ui/icons/iaAgent/TrashIcon"
import style from "./EditCompetitorModelNumbersModal.module.scss"

const { editCompetitorModelNumbersModal, stevensLabel, tagsContainer, inputSection, buttonsWrapper, formItem } = style

type Props = {
	isModalOpen: boolean
	setModalClose: () => void
	competitorName: string
	stevensModelNumber: string
	modelNumbers: string[]
	onSave: (modelNumbers: string[]) => void
}

export const EditCompetitorModelNumbersModal = ({
	isModalOpen,
	setModalClose,
	competitorName,
	stevensModelNumber,
	modelNumbers,
	onSave,
}: Props) => {
	const [form] = Form.useForm()
	const values = Form.useWatch([], form)
	const [currentModelNumbers, setCurrentModelNumbers] = useState<string[]>([])

	// Initialize model numbers when modal opens
	useEffect(() => {
		if (isModalOpen) {
			setCurrentModelNumbers([...modelNumbers])
			form.resetFields()
		}
	}, [isModalOpen, modelNumbers, form])

	const handleDeleteModelNumber = (modelNumber: string) => {
		setCurrentModelNumbers(currentModelNumbers.filter((num) => num !== modelNumber))
	}

	const handleAddModelNumber = () => {
		const trimmedValue = values?.modelNumber?.trim()

		// Check for duplicates
		if (currentModelNumbers.includes(trimmedValue)) {
			form.setFields([{ name: "modelNumber", errors: [`Model number "${trimmedValue}" already exists`] }])
			return
		}

		// Add the new model number
		setCurrentModelNumbers([...currentModelNumbers, trimmedValue])
		form.resetFields(["modelNumber"])
	}

	const handleSave = () => {
		onSave(currentModelNumbers)
	}

	const handleCancel = () => {
		setModalClose()
		form.resetFields()
	}

	const isChanged = JSON.stringify([...modelNumbers].sort()) !== JSON.stringify([...currentModelNumbers].sort())
	const inputValue = values?.modelNumber?.trim()
	const isSaveDisabled = !isChanged

	// Custom validator for duplicate model numbers
	const validateModelNumber = (_: unknown, value: string) => {
		// Allow empty values (field is optional)
		if (!value || !value.trim()) {
			return Promise.resolve()
		}
		if (currentModelNumbers.includes(value.trim())) {
			return Promise.reject(new Error(`Model number "${value.trim()}" already exists`))
		}
		return Promise.resolve()
	}

	return (
		<Modal isModalOpen={isModalOpen} setModalClose={handleCancel} title={`Edit ${competitorName} model numbers`}>
			<Form form={form}>
				<div className={editCompetitorModelNumbersModal}>
					<div className={stevensLabel}>Stevens: {stevensModelNumber}</div>
					<div className={tagsContainer}>
						{currentModelNumbers.map((modelNumber) => (
							<Tag key={modelNumber} closeIcon={<TrashIcon />} onClick={() => handleDeleteModelNumber(modelNumber)}>
								{modelNumber}
							</Tag>
						))}
					</div>
					<div className={inputSection}>
						<Form.Item name="modelNumber" rules={[{ validator: validateModelNumber }]} className={formItem}>
							<Input placeholder="Enter new model number" />
						</Form.Item>
						<ButtonPrimary onClick={handleAddModelNumber} submittable={!!inputValue}>
							Add
						</ButtonPrimary>
					</div>

					<div className={buttonsWrapper}>
						<ButtonSecondary onClick={handleCancel}>Cancel</ButtonSecondary>
						<ButtonPrimary submittable={!isSaveDisabled} onClick={handleSave}>
							Save
						</ButtonPrimary>
					</div>
				</div>
			</Form>
		</Modal>
	)
}
